import { useState } from 'react'
import { X, Mail } from 'lucide-react'
import type { Profile } from '../types'
// A2F-547: submit logic moved into a shared hook so both this modal (used
// from /install for the cross-device recovered-profile path) and the new
// inline SignupForm on / go through one codepath.
import { useSignup } from '../lib/useSignup'

export type TierChoice = 'free' | 'trial' | 'subscribe'

const STORAGE_KEY_PROFILE = 'chrona-pro:pending-profile'
const STORAGE_KEY_TIER = 'chrona-pro:tier-choice'
const STORAGE_KEY_METADATA = 'chrona-pro:pending-metadata'

export function persistPendingProfile(profile: Profile) {
  try {
    localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(profile))
  } catch {
    // localStorage may be disabled (private mode). Nothing sensible to do.
  }
}

export function persistPendingTier(tier: TierChoice) {
  try {
    localStorage.setItem(STORAGE_KEY_TIER, tier)
  } catch {
    // ignore
  }
}

export interface PendingMetadata {
  displayName?: string
  organization?: string
  roleTitle?: string
}

export function persistPendingMetadata(metadata: PendingMetadata): void {
  try {
    // Only persist if at least one real (non-email-default) field was
    // supplied — keeps the localStorage quiet when the user skipped the
    // optional fields entirely.
    const hasAny =
      !!metadata.displayName || !!metadata.organization || !!metadata.roleTitle
    if (!hasAny) {
      localStorage.removeItem(STORAGE_KEY_METADATA)
      return
    }
    localStorage.setItem(STORAGE_KEY_METADATA, JSON.stringify(metadata))
  } catch {
    // localStorage may be disabled; cross-device path still works via
    // pending-profile server write.
  }
}

export function readPendingSignup(): {
  profile: Profile | null
  tier: TierChoice | null
  metadata: PendingMetadata | null
} {
  try {
    const pr = localStorage.getItem(STORAGE_KEY_PROFILE)
    const tier = localStorage.getItem(STORAGE_KEY_TIER) as TierChoice | null
    const meta = localStorage.getItem(STORAGE_KEY_METADATA)
    return {
      profile: pr ? (JSON.parse(pr) as Profile) : null,
      tier,
      metadata: meta ? (JSON.parse(meta) as PendingMetadata) : null,
    }
  } catch {
    return { profile: null, tier: null, metadata: null }
  }
}

export function clearPendingMetadata(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_METADATA)
  } catch {
    // ignore
  }
}

/**
 * Clear the pending profile only. Called after the backend PUT succeeds
 * so a reload doesn't re-submit the same profile.
 */
export function clearPendingProfile() {
  try {
    localStorage.removeItem(STORAGE_KEY_PROFILE)
  } catch {
    // ignore
  }
}

/**
 * Clear the pending tier only. Called after the trial successfully
 * starts (or when tier was 'free' all along). A non-409 startTrial
 * failure MUST leave this in place so the user gets a retry path
 * instead of being silently dropped to the free tier.
 */
export function clearPendingTier() {
  try {
    localStorage.removeItem(STORAGE_KEY_TIER)
  } catch {
    // ignore
  }
}

interface Props {
  open: boolean
  profile: Profile
  onClose: () => void
}

export function SignupModal({ open, profile, onClose }: Props) {
  const { phase, error, submit: submitSignup } = useSignup()
  const [tier, setTier] = useState<TierChoice>('trial')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // A2F-540: optional identity fields. State preserved even while the
  // inputs are hidden for Phase 1 so flipping them back is a pure JSX
  // change. Empty string still falls back to the email default via
  // useSignup's internal trim logic.
  const [displayName, setDisplayName] = useState('')
  const [organization, setOrganization] = useState('')
  const [roleTitle, setRoleTitle] = useState('')
  const [terms, setTerms] = useState(false)

  if (!open) return null

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    await submitSignup({
      email,
      password,
      displayName,
      organization,
      roleTitle,
      tier,
      terms,
      profile,
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-brand-charcoal">
            {phase === 'sent' ? 'Check your email' : 'Get your ribbon'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-500 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        {phase === 'sent' ? (
          <div className="px-5 py-6 space-y-4">
            <div className="h-14 w-14 rounded-full bg-brand-orange/10 text-brand-orange grid place-items-center mx-auto">
              <Mail size={26} />
            </div>
            <p className="text-sm text-brand-charcoal text-center">
              We sent a confirmation link to <strong>{email}</strong>.
            </p>
            <p className="text-xs text-slate-600 text-center">
              Click the link in that email to activate your account. After
              confirming, you'll land on the install page with your ribbon
              ready to go.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 rounded-md bg-slate-100 text-brand-charcoal text-sm font-medium hover:bg-slate-200"
            >
              Got it
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-5 space-y-4">
            <fieldset className="space-y-2">
              <legend className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">
                Choose a plan
              </legend>
              <TierOption
                value="free"
                current={tier}
                onChange={setTier}
                title="Free"
                subtitle="Formatting tools, free forever."
              />
              <TierOption
                value="trial"
                current={tier}
                onChange={setTier}
                title="Start 14-day trial"
                subtitle="Everything unlocked. No card required."
              />
              <TierOption
                value="subscribe"
                current={tier}
                onChange={setTier}
                title="Subscribe"
                subtitle="Full access. Payment coming soon — starts as a trial today."
              />
            </fieldset>

            <label className="block">
              <span className="text-xs font-medium text-slate-600">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40"
                placeholder="you@company.com"
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-slate-600">Password</span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40"
                placeholder="At least 8 characters"
              />
            </label>

            {/* A2F-540: optional identity fields hidden for Phase 1 — ship
                minimum-friction signup, measure conversion, then A/B test
                re-enabling. State + pass-through logic below are preserved
                so flipping this back is just re-rendering the three inputs.
                Empty string state gracefully falls back to the email default
                at submit time (backend's min_length:1 still satisfied). */}

            <label className="flex items-start gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I accept the{' '}
                <a
                  href="https://chronabio.ai/terms"
                  target="_blank"
                  rel="noopener"
                  className="underline hover:text-slate-700"
                >
                  terms of service
                </a>{' '}
                and{' '}
                <a
                  href="https://chronabio.ai/privacy"
                  target="_blank"
                  rel="noopener"
                  className="underline hover:text-slate-700"
                >
                  privacy policy
                </a>
                .
              </span>
            </label>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={phase === 'submitting'}
              className="w-full py-2.5 rounded-md bg-brand-orange text-white text-sm font-medium hover:bg-brand-orange/90 disabled:opacity-60"
            >
              {phase === 'submitting' ? 'Creating account…' : 'Create account'}
            </button>

            <p className="text-[11px] text-slate-500 text-center">
              We'll email you a confirmation link. You'll need to click it
              before you can install.
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

function TierOption({
  value,
  current,
  onChange,
  title,
  subtitle,
}: {
  value: TierChoice
  current: TierChoice
  onChange: (v: TierChoice) => void
  title: string
  subtitle: string
}) {
  const active = current === value
  return (
    <label
      className={`block rounded-md border px-3 py-2 cursor-pointer transition-colors ${
        active
          ? 'border-brand-orange bg-brand-orange/5'
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <input
        type="radio"
        name="tier"
        value={value}
        checked={active}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      <div className="text-sm font-medium text-brand-charcoal">{title}</div>
      <div className="text-xs text-slate-600">{subtitle}</div>
    </label>
  )
}
